import type React from 'react';

import { CalendarTodayRounded, PhoneDisabledRounded } from '@mui/icons-material';
import {
  FiqlFilters,
  FiqlInput,
  OrdContextMenuAction,
  OrdDatagrid,
  OrdFormList,
  OrdList,
  OrdInfoIcon,
  OrdIconStandard,
  OrdMultilineField,
  OrdChipInfo,
} from '@ragu-components/ord-components';
import { CallSessionFields } from '@ragu-components/ord-types';
import { ChipField, FunctionField, TextField, useTranslate } from 'react-admin';

import { isoToDate, isoToTime, isoDuration, isoToDateTime } from 'src/lib/timeUtils';

import type { OrdChipInfoType, OrdInfoIconType, OrdListProps } from '@ragu-components/ord-components';
import type { CallSession } from '@ragu-components/ord-types';

const SUCCESS_CAUSES = [
  'NORMAL_CLEARING',
  'NORMAL_UNSPECIFIED',
  'SUCCESS',
  'INTERWORKING',
  'ATTENDED_TRANSFER',
  'BLIND_TRANSFER',
  'PICKED_OFF',
  'LOSE_RACE',
];

const WARNING_CAUSES = [
  'NO_ANSWER',
  'NO_USER_RESPONSE',
  'ALLOTTED_TIMEOUT',
  'ORIGINATOR_CANCEL',
  'USER_BUSY',
  'BUSY_EVERYWHERE',
  'NORMAL_CIRCUIT_CONGESTION',
  'SUBSCRIBER_ABSENT',
  'NO_PICKUP',
  'REDIRECTION_TO_NEW_DESTINATION',
];

export const getHangupCauseType = (hangupCause: string | null | undefined): OrdInfoIconType => {
  if (hangupCause == null) return 'unknown';
  if (SUCCESS_CAUSES.includes(hangupCause)) return 'success';
  if (WARNING_CAUSES.includes(hangupCause)) return 'warning';
  return 'error';
};

const dateRangeFields = [
  <FiqlInput
    key='creation_time_after'
    type='date-time'
    source='creation_time_after'
    fiqlField={CallSessionFields.creation_time}
    operator='=gt='
    label='ord.filters.range.after'
  />,
  <FiqlInput
    key='creation_time_before'
    type='date-time'
    source='creation_time_before'
    fiqlField={CallSessionFields.creation_time}
    operator='=lt='
    label='ord.filters.range.before'
  />,
];

export const CallSessionsList = (props: Partial<OrdListProps>): React.ReactElement => {
  const translate = useTranslate();

  const quickFilterFields = [
    <FiqlInput key='hangup_cause' type='enum' autocomplete source={CallSessionFields.hangup_cause} />,
    ...dateRangeFields,
  ];

  const menuActions = [
    <OrdContextMenuAction key='filter'>
      <FiqlFilters>
        <OrdFormList icon={<PhoneDisabledRounded />}>
          <FiqlInput type='enum' autocomplete source={CallSessionFields.hangup_cause} />
        </OrdFormList>
        <OrdFormList icon={<CalendarTodayRounded />}>{dateRangeFields}</OrdFormList>
      </FiqlFilters>
    </OrdContextMenuAction>,
  ];

  return (
    <OrdList
      icon={<OrdIconStandard name='callSessions' />}
      hasCreate={false}
      hasFilters={false}
      menuActions={menuActions}
      quickFilters={quickFilterFields}
      sort={{ field: CallSessionFields.creation_time, order: 'DESC' }}
      {...props}
    >
      <OrdDatagrid bulkActionButtons={false} showOrdOpenButton>
        <FunctionField
          label=''
          sortable={false}
          cellClassName='hangup-cause-cell'
          render={(record: CallSession) => (
            <OrdInfoIcon
              type={getHangupCauseType(record.hangup_cause)}
              tooltip={translate(`resources.call_sessions.enums.hangup_cause.${record.hangup_cause}`)}
            />
          )}
        />
        <OrdMultilineField
          source={CallSessionFields.caller_id_name}
          label='resources.call_sessions.fields.caller'
          renderTop={() => <TextField source={CallSessionFields.caller_id_name} />}
          renderBottom={() => <ChipField source={CallSessionFields.caller_id_number} />}
          renderCompact={(record: CallSession) =>
            record.caller_id_name !== null ? (
              <TextField source={CallSessionFields.caller_id_name} />
            ) : (
              <ChipField source={CallSessionFields.caller_id_number} />
            )
          }
        />
        <OrdMultilineField
          source={CallSessionFields.called_id_name}
          label='resources.call_sessions.fields.called'
          renderTop={() => <TextField source={CallSessionFields.called_id_name} />}
          renderBottom={() => <ChipField source={CallSessionFields.called_id_number} />}
          renderCompact={(record: CallSession) =>
            record.called_id_name !== null ? (
              <TextField source={CallSessionFields.called_id_name} />
            ) : (
              <ChipField source={CallSessionFields.called_id_number} />
            )
          }
        />
        <OrdMultilineField
          source={CallSessionFields.creation_time}
          renderTop={(record: CallSession) => (record.creation_time != null ? isoToDate(record.creation_time) : '')}
          renderBottom={(record: CallSession) => (record.creation_time != null ? isoToTime(record.creation_time) : '')}
          renderCompact={(record: CallSession) =>
            record.creation_time != null ? isoToDateTime(record.creation_time) : ''
          }
        />
        <OrdMultilineField
          source={CallSessionFields.answer_time}
          renderTop={(record: CallSession) => (record.answer_time != null ? isoToDate(record.answer_time) : '')}
          renderBottom={(record: CallSession) => (record.answer_time != null ? isoToTime(record.answer_time) : '')}
          renderCompact={(record: CallSession) => (record.answer_time != null ? isoToDateTime(record.answer_time) : '')}
        />
        <FunctionField
          source={CallSessionFields.hangup_time}
          label='resources.call_sessions.fields.duration'
          render={(record: CallSession) =>
            record.answer_time != null && record.hangup_time != null
              ? isoDuration(record.answer_time, record.hangup_time)
              : ''
          }
        />
        <FunctionField
          source={CallSessionFields.hangup_cause}
          sortable={false}
          render={(record: CallSession) =>
            record.hangup_cause != null ? (
              <OrdChipInfo
                type={getHangupCauseType(record.hangup_cause) as OrdChipInfoType}
                label={translate(`resources.call_sessions.enums.hangup_cause.${record.hangup_cause}`)}
              />
            ) : null
          }
        />
      </OrdDatagrid>
    </OrdList>
  );
};

export default CallSessionsList;
